import React from 'react';
import { FileText, Download } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

// Department-wise Government Orders / Guidelines for the NOC / registration
// process. To publish a G.O.: put the PDF in `public/go-docs/` and add an
// entry to that department's `docs` list, e.g.
//   { title: ['G.O. on …', 'அரசாணை …'], goNo: 'G.O.(Ms) No. 123', date: '01.10.2026', file: '/go-docs/wrd-go-123.pdf' }
const DEPARTMENTS = [
  { code: 'WRD',    name: ['Water Resources Department', 'நீர்வளத் துறை'],                                             docs: [
      { title: ['Government Order on NOC for restoration of water bodies', 'நீர்நிலைகள் புனரமைப்புக்கான தடையின்மைச் சான்று தொடர்பான அரசாணை'], goNo: '', date: '', file: '/go-docs/wrd-go.pdf' },
    ] },
  { code: 'RDPR',   name: ['Rural Development and Panchayat Raj', 'ஊரக வளர்ச்சி மற்றும் ஊராட்சித் துறை'],                   docs: [
      { title: ['Government Order on NOC for restoration of water bodies', 'நீர்நிலைகள் புனரமைப்புக்கான தடையின்மைச் சான்று தொடர்பான அரசாணை'], goNo: '', date: '', file: '/go-docs/rdpr-go.pdf' },
    ] },
  { code: 'DTP',    name: ['Directorate of Town Panchayats', 'பேரூராட்சிகள் இயக்குநரகம்'],                                    docs: [] },
  { code: 'DMA',    name: ['Municipal Administration', 'நகராட்சி நிர்வாக இயக்குநரகம்'],                                       docs: [] },
  { code: 'GCC',    name: ['Greater Chennai Corporation', 'பெருநகர சென்னை மாநகராட்சி'],                                  docs: [] },
  { code: 'HRCE',   name: ['Hindu Religious & Charitable Endowments', 'இந்து சமய அறநிலையத் துறை'],                       docs: [
      { title: ['Government Order on NOC for restoration of water bodies', 'நீர்நிலைகள் புனரமைப்புக்கான தடையின்மைச் சான்று தொடர்பான அரசாணை'], goNo: '', date: '', file: '/go-docs/hrce-go.pdf' },
    ] },
  { code: 'FOREST', name: ['Environment, Climate Change & Forest', 'சுற்றுச்சூழல், காலநிலை மாற்றம் மற்றும் வனத் துறை'],    docs: [
      { title: ['Government Order on NOC for restoration of water bodies', 'நீர்நிலைகள் புனரமைப்புக்கான தடையின்மைச் சான்று தொடர்பான அரசாணை'], goNo: '', date: '', file: '/go-docs/forest-go.pdf' },
    ] },
];

const GuidelinesPage = () => {
  const { tr } = useLang();

  return (
    <div className="min-h-screen bg-slate-100 pt-20">
      {/* Page header */}
      <div className="relative bg-cover bg-center border-b-4 border-brand-accent" style={{ backgroundImage: "url('/images/water3.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-700/95 via-brand-600/85 to-teal-500/70" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <h1 className="text-3xl lg:text-4xl font-semibold text-white">
            {tr('Government Orders & Guidelines', 'அரசாணைகள் மற்றும் வழிகாட்டுதல்கள்')}
          </h1>
          <p className="mt-2 text-white/90 max-w-2xl">
            {tr(
              'Department-wise Government Orders / Guidelines issued for the NOC and registration process.',
              'தடையின்மைச் சான்று மற்றும் பதிவு நடைமுறைக்காகத் துறை வாரியாக வெளியிடப்பட்ட அரசாணைகள் / வழிகாட்டுதல்கள்.'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 space-y-4">
        {DEPARTMENTS.map((dept) => (
          <section key={dept.code} className="bg-white border border-slate-200">
            <div className="px-5 py-3 bg-brand-600 flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-white">{tr(...dept.name)}</h2>
              <span className="text-xs font-semibold bg-white/20 text-white px-2 py-0.5">{dept.code}</span>
            </div>
            {dept.docs.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500 italic">
                {tr('Government Orders / Guidelines will be published here shortly.', 'அரசாணைகள் / வழிகாட்டுதல்கள் விரைவில் இங்கு வெளியிடப்படும்.')}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {dept.docs.map((doc) => (
                  <li key={doc.file} className="flex items-center justify-between gap-4 px-5 py-3 odd:bg-white even:bg-brand-50">
                    <div className="flex items-start gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">{tr(...doc.title)}</p>
                        <p className="text-xs text-slate-500">{[doc.goNo, doc.date].filter(Boolean).join(' · ')}</p>
                      </div>
                    </div>
                    <a
                      href={doc.file}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 border border-brand-600 hover:bg-brand-50 flex-shrink-0"
                    >
                      <Download className="w-4 h-4" /> {tr('View', 'காண்க')}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default GuidelinesPage;
